import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';

// API Response Interfaces
export interface WarehouseWarrantResponse {
  pageContent: WarehouseWarrantRecord[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  empty: boolean;
  numberOfElements: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface WarehouseResultResponse {
  pageContent: WarehouseResultRecord[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  empty: boolean;
  numberOfElements: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// Inbound Notice (Warehouse Warrant) Record
export interface WarehouseWarrantRecord {
  id: number;
  code: string;
  platformOrderCode: string;
  orderCode: string;
  frontRecordType: string;
  frontRecordId: number;
  frontRecordCode: string;
  inputType: string;
  status: string;
  qty: number;
  receivingLogicWarehouseName: string;
  receivingLogicWarehouseCode: string;
  storeName: string;
  channelTypeCode: string;
  creationDate: string;
  inputTypeMeaning: string | null;
  statusMeaning: string | null;
  channelTypeCodeMeaning: string | null;
}

// Legacy interface for backward compatibility
export interface InboundRecord extends WarehouseWarrantRecord {
  serialNumber: number;
  warehouseReceiptNumber: string;
  internalOrderNumber: string;
  prefixOrderNumber: string;
  thirdPartyOrderNumber: string;
  warehousingType: string;
  warehouseStatus: string;
  logicalWarehouseCode: string;
  quantityRequired: number;
  logicalWarehouseName: string;
  salesStore: string;
  salesChannels: string;
  creationTime: string;
}

export const INBOUND_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'queryCode',
    label: 'inbound.search.order_number',
    type: 'text',
    placeholder: 'inbound.search.order_number_placeholder',
  },
  {
    key: 'status',
    label: 'inbound.search.inbound_status',
    type: 'select',
    placeholder: 'inbound.search.status_placeholder',
    options: [], // Will be populated from API
  },
  {
    key: 'receivingLogicWarehouseCode',
    label: 'inbound.search.receiving_warehouse_code',
    type: 'text',
    placeholder: 'inbound.search.receiving_warehouse_code_placeholder',
  },
  {
    key: 'creationTime',
    label: 'inbound.search.creation_time',
    type: 'dateRange',
    range: { startKey: 'beginCreationDate', endKey: 'endCreationDate' },
  },
];

// ============================================================================
// INBOUND WARRANT TABLE CONFIGURATION
// ============================================================================

/**
 * Default column property names for Inbound Warrant table
 */
export const INBOUND_WARRANT_DEFAULT_COLUMNS = {
  CODE: 'code',
  FRONT_RECORD_CODE: 'frontRecordCode',
  INPUT_TYPE: 'inputType',
  STATUS: 'status',
  QTY: 'qty',
  RECEIVING_LOGIC_WAREHOUSE_CODE: 'receivingLogicWarehouseCode',
  RECEIVING_LOGIC_WAREHOUSE_NAME: 'receivingLogicWarehouseName',
  CREATION_DATE: 'creationDate',
} as const;

/**
 * Flexible column property names for Inbound Warrant table
 */
export const INBOUND_WARRANT_FLEXIBLE_COLUMNS = {
  // No flexible columns
} as const;

/**
 * Action column property name for Inbound Warrant table
 */
export const INBOUND_WARRANT_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order for Inbound Warrant table
 */
export const INBOUND_WARRANT_DEFAULT_COLUMNS_ORDER: string[] = [
  INBOUND_WARRANT_DEFAULT_COLUMNS.CODE,
  INBOUND_WARRANT_DEFAULT_COLUMNS.FRONT_RECORD_CODE,
  INBOUND_WARRANT_DEFAULT_COLUMNS.INPUT_TYPE,
  INBOUND_WARRANT_DEFAULT_COLUMNS.STATUS,
  INBOUND_WARRANT_DEFAULT_COLUMNS.QTY,
  INBOUND_WARRANT_DEFAULT_COLUMNS.RECEIVING_LOGIC_WAREHOUSE_CODE,
  INBOUND_WARRANT_DEFAULT_COLUMNS.RECEIVING_LOGIC_WAREHOUSE_NAME,
  INBOUND_WARRANT_DEFAULT_COLUMNS.CREATION_DATE,
];

/**
 * Flexible columns array for Inbound Warrant table
 */
export const INBOUND_WARRANT_FLEXIBLE_COLUMNS_ORDER: string[] = [];

export const INBOUND_TABLE_CONFIG: TableColumn[] = [
  {
    label: 'inbound.table.columns.inbound_warrant_code',
    prop: INBOUND_WARRANT_DEFAULT_COLUMNS.CODE,
    width: 200,
  },
  {
    label: 'inbound.table.columns.preceding_document_number',
    prop: INBOUND_WARRANT_DEFAULT_COLUMNS.FRONT_RECORD_CODE,
    width: 200,
  },
  {
    label: 'inbound.table.columns.inbound_type',
    prop: INBOUND_WARRANT_DEFAULT_COLUMNS.INPUT_TYPE,
    width: 160,
    type: 'custom',
  },
  {
    label: 'inbound.table.columns.status',
    prop: INBOUND_WARRANT_DEFAULT_COLUMNS.STATUS,
    width: 180,
    type: 'custom',
    align: 'center',
  },
  {
    label: 'inbound.table.columns.required_inbound_quantity',
    prop: INBOUND_WARRANT_DEFAULT_COLUMNS.QTY,
    width: 180,
    type: 'text',
    align: 'right',
  },
  {
    label: 'inbound.table.columns.receiving_logical_warehouse_code',
    prop: INBOUND_WARRANT_DEFAULT_COLUMNS.RECEIVING_LOGIC_WAREHOUSE_CODE,
    width: 220,
  },
  {
    label: 'inbound.table.columns.receiving_logical_warehouse_name',
    prop: INBOUND_WARRANT_DEFAULT_COLUMNS.RECEIVING_LOGIC_WAREHOUSE_NAME,
    width: 200,
  },
  {
    label: 'inbound.table.columns.creation_time',
    prop: INBOUND_WARRANT_DEFAULT_COLUMNS.CREATION_DATE,
    width: 180,
    type: 'custom',
    align: 'center',
    sortable: true,
  },
  {
    label: 'inbound.table.columns.actions',
    prop: INBOUND_WARRANT_ACTION_COLUMN.ACTIONS,
    type: 'actions',
    width: 120,
    align: 'center',
  },
];

// ============================================================================
// INBOUND NOTICE TABLE CONFIGURATION
// ============================================================================

/**
 * Default column property names for Inbound Notice table
 */
export const INBOUND_NOTICE_DEFAULT_COLUMNS = {
  CODE: 'code',
  SOURCE_CODE: 'sourceCode',
  WAREHOUSE_NAME: 'warehouseName',
  EXPECTED_ARRIVAL_DATE: 'expectedArrivalDate',
  STATUS: 'status',
  CREATION_DATE: 'creationDate',
} as const;

/**
 * Flexible column property names for Inbound Notice table
 */
export const INBOUND_NOTICE_FLEXIBLE_COLUMNS = {
  // No flexible columns
} as const;

/**
 * Action column property name for Inbound Notice table
 */
export const INBOUND_NOTICE_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order for Inbound Notice table
 */
export const INBOUND_NOTICE_DEFAULT_COLUMNS_ORDER: string[] = [
  INBOUND_NOTICE_DEFAULT_COLUMNS.CODE,
  INBOUND_NOTICE_DEFAULT_COLUMNS.SOURCE_CODE,
  INBOUND_NOTICE_DEFAULT_COLUMNS.WAREHOUSE_NAME,
  INBOUND_NOTICE_DEFAULT_COLUMNS.EXPECTED_ARRIVAL_DATE,
  INBOUND_NOTICE_DEFAULT_COLUMNS.STATUS,
  INBOUND_NOTICE_DEFAULT_COLUMNS.CREATION_DATE,
];

/**
 * Flexible columns array for Inbound Notice table
 */
export const INBOUND_NOTICE_FLEXIBLE_COLUMNS_ORDER: string[] = [];

export const INBOUND_NOTICE_TABLE_CONFIG: TableColumn[] = [
  {
    label: 'inbound.table.columns.notice_code',
    prop: INBOUND_NOTICE_DEFAULT_COLUMNS.CODE,
    width: 200,
  },
  {
    label: 'inbound.table.columns.internal_order_number',
    prop: INBOUND_NOTICE_DEFAULT_COLUMNS.SOURCE_CODE,
    width: 200,
  },
  {
    label: 'inbound.table.columns.warehouse_name',
    prop: INBOUND_NOTICE_DEFAULT_COLUMNS.WAREHOUSE_NAME,
    width: 200,
    type: 'custom',
  },
  {
    label: 'inbound.table.columns.delivery_date',
    prop: INBOUND_NOTICE_DEFAULT_COLUMNS.EXPECTED_ARRIVAL_DATE,
    width: 150,
    type: 'custom',
  },
  {
    label: 'inbound.table.columns.status',
    prop: INBOUND_NOTICE_DEFAULT_COLUMNS.STATUS,
    width: 180,
    type: 'custom',
    align: 'center',
  },
  {
    label: 'inbound.table.columns.creation_time',
    prop: INBOUND_NOTICE_DEFAULT_COLUMNS.CREATION_DATE,
    width: 180,
    type: 'custom',
    sortable: true,
  },
  {
    label: 'inbound.table.columns.actions',
    prop: INBOUND_NOTICE_ACTION_COLUMN.ACTIONS,
    type: 'actions',
    width: 120,
    align: 'center',
  },
];

export const INBOUND_DEFAULT_CONFIG = {
  PAGE_SIZE: 20,
} as const;

// Inbound Result (Warehouse Result) Record
export interface WarehouseResultRecord {
  id: number;
  code: string;
  warehouseWarrantCode: string;
  platformOrderCode: string; // Third-Party Order Number
  orderCode: string; // Internal Order Number
  frontRecordCode: string; // Preceding Document Number
  inputType: string;
  inputTypeMeaning: string;
  status: string;
  statusMeaning: string;
  receivingLogicWarehouseName: string;
  receivingLogicWarehouseCode: string;
  storeName: string;
  channelTypeCode: string;
  channelTypeCodeMeaning: string;
  qty: number; // Required Inbound Quantity
  alreadyQty: number; // Received Quantity
  creationDate: string;
}

export const WAREHOUSE_RESULT_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'queryCode',
    label: 'inbound.search.order_number',
    type: 'text',
    placeholder: 'inbound.search.result_order_number_placeholder',
  },
  {
    key: 'channelTypeCode',
    label: 'inbound.search.sales_channel',
    type: 'text',
    placeholder: 'inbound.search.sales_channel_placeholder',
  },
  {
    key: 'storeIdList',
    label: 'inbound.search.sales_store',
    type: 'text',
    placeholder: 'inbound.search.sales_store_placeholder',
  },
  {
    key: 'statuses',
    label: 'inbound.search.inbound_status',
    type: 'multiselect',
    placeholder: 'inbound.search.status_placeholder',
    options: [], // Will be populated from API
  },
  {
    key: 'receivingLogicWarehouseCode',
    label: 'inbound.search.receiving_warehouse_code',
    type: 'text',
    placeholder: 'inbound.search.receiving_warehouse_code_placeholder',
  },
  {
    key: 'creationTime',
    label: 'inbound.search.creation_time',
    type: 'dateRange',
    range: { startKey: 'beginCreationDate', endKey: 'endCreationDate' },
  },
];

// ============================================================================
// INBOUND RESULT TABLE CONFIGURATION
// ============================================================================

/**
 * Default column property names for Inbound Result table
 */
export const INBOUND_RESULT_DEFAULT_COLUMNS = {
  CODE: 'code',
  WAREHOUSE_WARRANT_CODE: 'warehouseWarrantCode',
  FRONT_RECORD_CODE: 'frontRecordCode',
  INPUT_TYPE: 'inputType',
  QTY: 'qty',
  ALREADY_QTY: 'alreadyQty',
  RECEIVING_LOGIC_WAREHOUSE_CODE: 'receivingLogicWarehouseCode',
  RECEIVING_LOGIC_WAREHOUSE_NAME: 'receivingLogicWarehouseName',
  CREATION_DATE: 'creationDate',
} as const;

/**
 * Flexible column property names for Inbound Result table
 */
export const INBOUND_RESULT_FLEXIBLE_COLUMNS = {
  // No flexible columns
} as const;

/**
 * Action column property name for Inbound Result table
 */
export const INBOUND_RESULT_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order for Inbound Result table
 */
export const INBOUND_RESULT_DEFAULT_COLUMNS_ORDER: string[] = [
  INBOUND_RESULT_DEFAULT_COLUMNS.CODE,
  INBOUND_RESULT_DEFAULT_COLUMNS.WAREHOUSE_WARRANT_CODE,
  INBOUND_RESULT_DEFAULT_COLUMNS.FRONT_RECORD_CODE,
  INBOUND_RESULT_DEFAULT_COLUMNS.INPUT_TYPE,
  INBOUND_RESULT_DEFAULT_COLUMNS.QTY,
  INBOUND_RESULT_DEFAULT_COLUMNS.ALREADY_QTY,
  INBOUND_RESULT_DEFAULT_COLUMNS.RECEIVING_LOGIC_WAREHOUSE_CODE,
  INBOUND_RESULT_DEFAULT_COLUMNS.RECEIVING_LOGIC_WAREHOUSE_NAME,
  INBOUND_RESULT_DEFAULT_COLUMNS.CREATION_DATE,
];

/**
 * Flexible columns array for Inbound Result table
 */
export const INBOUND_RESULT_FLEXIBLE_COLUMNS_ORDER: string[] = [];

export const INBOUND_RESULT_TABLE_CONFIG: TableColumn[] = [
  {
    label: 'inbound.table.columns.inbound_result_number',
    prop: INBOUND_RESULT_DEFAULT_COLUMNS.CODE,
    width: 200,
  },
  {
    label: 'inbound.table.columns.inbound_warrant_number',
    prop: INBOUND_RESULT_DEFAULT_COLUMNS.WAREHOUSE_WARRANT_CODE,
    width: 200,
  },
  {
    label: 'inbound.table.columns.preceding_document_number',
    prop: INBOUND_RESULT_DEFAULT_COLUMNS.FRONT_RECORD_CODE,
    width: 200,
  },
  {
    label: 'inbound.table.columns.inbound_type',
    prop: INBOUND_RESULT_DEFAULT_COLUMNS.INPUT_TYPE,
    width: 160,
    type: 'custom',
  },
  {
    label: 'inbound.table.columns.required_inbound_quantity',
    prop: INBOUND_RESULT_DEFAULT_COLUMNS.QTY,
    width: 180,
    type: 'text',
    align: 'right',
  },
  {
    label: 'inbound.table.columns.received_quantity',
    prop: INBOUND_RESULT_DEFAULT_COLUMNS.ALREADY_QTY,
    width: 180,
    type: 'text',
    align: 'right',
  },
  {
    label: 'inbound.table.columns.receiving_logical_warehouse_code',
    prop: INBOUND_RESULT_DEFAULT_COLUMNS.RECEIVING_LOGIC_WAREHOUSE_CODE,
    width: 220,
  },
  {
    label: 'inbound.table.columns.receiving_logical_warehouse_name',
    prop: INBOUND_RESULT_DEFAULT_COLUMNS.RECEIVING_LOGIC_WAREHOUSE_NAME,
    width: 200,
  },
  {
    label: 'inbound.table.columns.creation_time',
    prop: INBOUND_RESULT_DEFAULT_COLUMNS.CREATION_DATE,
    width: 180,
    type: 'custom',
    align: 'center',
    sortable: true,
  },
  {
    label: 'inbound.table.columns.actions',
    prop: INBOUND_RESULT_ACTION_COLUMN.ACTIONS,
    type: 'actions',
    width: 100,
    align: 'center',
  },
];

// Enums for Inbound Types and Status
export enum InboundType {
  PURCHASE_ORDER = 'PO',
  TRANSFER_ORDER = 'TO',
  RETURN_ORDER = 'RO',
}

export enum InboundNoticeStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  SHIPPED = 'SHIPPED',
  IN_TRANSIT = 'IN_TRANSIT',
  ARRIVED = 'ARRIVED',
  RECEIVING = 'RECEIVING',
  PARTIAL_RECEIVED = 'PARTIAL_RECEIVED',
  RECEIVED = 'RECEIVED',
  CANCELLED = 'CANCELLED',
}

export enum InboundWarrantStatus {
  CREATED = 'CREATED',
  CONFIRMED = 'CONFIRMED',
  IN_TRANSIT = 'IN_TRANSIT',
  ARRIVED = 'ARRIVED',
  RECEIVING = 'RECEIVING',
  PARTIALLY_RECEIVED = 'PARTIALLY_RECEIVED',
  RECEIVED = 'RECEIVED',
  CANCELLED = 'CANCELLED',
  PENDING = 'PENDING',
  INSPECTED = 'INSPECTED',
  COMPLETED = 'COMPLETED',
}

export enum InboundResultStatus {
  PENDING = 'PENDING',
  CREATED = 'CREATED',
  IN_TRANSIT = 'IN_TRANSIT',
  RECEIVED = 'RECEIVED',
  INSPECTED = 'INSPECTED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

// Status Options for UI - Only 4 statuses for inbound notice
export const INBOUND_NOTICE_STATUS_OPTIONS = [
  { label: 'inbound.status.pending', value: InboundNoticeStatus.PENDING }, // Đang xử lý
  { label: 'inbound.status.receiving', value: InboundNoticeStatus.RECEIVING }, // Đang nhận hàng
  { label: 'inbound.status.received', value: InboundNoticeStatus.RECEIVED }, // Đã nhận hàng
  { label: 'inbound.status.cancelled', value: InboundNoticeStatus.CANCELLED }, // Huỷ
];

export const INBOUND_WARRANT_STATUS_OPTIONS = [
  { label: 'inbound.status.created', value: InboundWarrantStatus.CREATED },
  { label: 'inbound.status.confirmed', value: InboundWarrantStatus.CONFIRMED },
  {
    label: 'inbound.status.partially_received',
    value: InboundWarrantStatus.PARTIALLY_RECEIVED,
  },
  { label: 'inbound.status.received', value: InboundWarrantStatus.RECEIVED },
  { label: 'inbound.status.cancelled', value: InboundWarrantStatus.CANCELLED },
];

export const INBOUND_TYPE_OPTIONS = [
  {
    label: 'Purchase Order',
    value: InboundType.PURCHASE_ORDER,
    description: 'Supplier → Warehouse',
  },
  {
    label: 'Transfer Order',
    value: InboundType.TRANSFER_ORDER,
    description: 'Warehouse → Store',
  },
  {
    label: 'Return Order',
    value: InboundType.RETURN_ORDER,
    description: 'Customer → Warehouse',
  },
];

export const INBOUND_MESSAGES = {
  ERROR: {
    DATA_LOAD_FAILED: 'Failed to load inbound data',
    INBOUND_RESULT_LOAD_FAILED: 'Failed to load inbound result data',
  },
} as const;
